/**
 * Funnel Campaign — section-level campaign pricing with sticky session.
 *
 * Mechanism:
 *  - A product-list section with `data-campaign-config` on its wrapper is treated
 *    as an active campaign on the current page. JS reads the config and rewrites
 *    every <product-price> inside it (if the product's tags contain `cfg.tag`).
 *  - When the visitor clicks any <a href="/products/..."> inside such a section,
 *    the config is saved to localStorage with a timestamp ("sticky").
 *  - On any subsequent page (PDP, collection, etc.), if a valid sticky config
 *    exists, it is applied to all <product-price> elements matching `cfg.tag`.
 *  - The inline blocking script in <head> sets `html.cmp-sticky` when sticky is
 *    valid, which together with the section's `data-campaign-config` triggers
 *    the CSS rule that hides <product-price> until JS marks it `data-cmp-applied`.
 *    This avoids the flash of the original price before the swap.
 *
 * No URL parameters are used — the config travels via localStorage only.
 * Persistence window is per-section (`persistHours`).
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'cmp_funnel';
  const APPLIED_ATTR = 'data-cmp-applied';

  // --- Sticky storage ------------------------------------------------------

  // Parse "key1:value1,key2:value2" → [{key, value}, ...]
  function parseExtraStorage(spec) {
    if (!spec || typeof spec !== 'string') return [];
    const out = [];
    spec.split(',').forEach((pair) => {
      const idx = pair.indexOf(':');
      if (idx === -1) return;
      const key = pair.slice(0, idx).trim();
      const value = pair.slice(idx + 1).trim();
      if (key) out.push({ key, value });
    });
    return out;
  }

  function cleanupExtraKeys(extraKeys) {
    if (!extraKeys || !extraKeys.length) return;
    extraKeys.forEach((k) => {
      try { localStorage.removeItem(k); } catch (_) {}
    });
  }

  function readSticky() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.ts || !data.tag) return null;
      const persistMs = (Number(data.persistHours) || 24) * 3600 * 1000;
      const age = Date.now() - Number(data.ts);
      if (age < 0 || age > persistMs) {
        cleanupExtraKeys(data.extraKeys);
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      return null;
    }
  }

  function writeSticky(cfg) {
    try {
      const extras = parseExtraStorage(cfg.extraStorage);
      extras.forEach(({ key, value }) => {
        try { localStorage.setItem(key, value); } catch (_) {}
      });
      const payload = Object.assign({}, cfg, {
        ts: Date.now(),
        extraKeys: extras.map((e) => e.key),
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) { /* swallow */ }
  }

  // --- Currency formatting -------------------------------------------------

  function formatMoney(cents, currency) {
    const amount = Number(cents) / 100;
    const locale = currency === 'EUR' ? 'sk-SK' : 'cs-CZ';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'CZK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (e) {
      // Fallback: CZ-style integer with currency symbol
      return Math.round(amount).toLocaleString('cs-CZ') + ' ' + (currency || 'CZK');
    }
  }

  // --- Apply campaign to a single <product-price> --------------------------

  function applyToProductPrice(productPrice, cfg) {
    if (!productPrice || productPrice.hasAttribute(APPLIED_ATTR)) return;
    const tagsAttr = productPrice.getAttribute('data-product-tags') || '';
    const tags = tagsAttr.split(',').map((t) => t.trim()).filter(Boolean);
    if (!tags.includes(cfg.tag)) {
      // Product is not in this campaign — mark applied so CSS unhides it.
      productPrice.setAttribute(APPLIED_ATTR, 'skip');
      return;
    }

    const priceCents = Number(productPrice.getAttribute('data-product-price-cents'));
    const compareCents = Number(productPrice.getAttribute('data-product-compare-cents'));
    const currency = productPrice.getAttribute('data-product-currency') || 'CZK';

    if (!priceCents || priceCents <= 0) {
      productPrice.setAttribute(APPLIED_ATTR, 'skip');
      return;
    }

    // Don't double-apply if the product already has a real sale (compare > price)
    if (compareCents && compareCents > priceCents) {
      productPrice.setAttribute(APPLIED_ATTR, 'skip');
      return;
    }

    // Compute sale price + the human-readable "{discount}" string,
    // branching on discountType ("fixed" amount vs "percent").
    let saleCents;
    let discountStr;
    if (cfg.discountType === 'fixed') {
      // Per-currency amount: EUR market uses discountAmountEur (if non-zero), CZK uses discountAmount.
      let amount = Number(cfg.discountAmount) || 0;
      if (currency === 'EUR') {
        const eur = Number(cfg.discountAmountEur) || 0;
        if (eur > 0) amount = eur;
      }
      const fixedCents = Math.max(0, Math.round(amount * 100));
      saleCents = Math.max(0, priceCents - fixedCents);
      discountStr = formatMoney(fixedCents, currency);
    } else {
      const pct = Math.max(0, Math.min(100, Number(cfg.pct) || 0));
      saleCents = Math.round(priceCents * (100 - pct) / 100);
      discountStr = pct + ' %';
    }
    const discountCents = priceCents - saleCents;

    const container = productPrice.querySelector('[ref="priceContainer"]');
    if (!container) {
      productPrice.setAttribute(APPLIED_ATTR, 'no-container');
      return;
    }

    // Apply CSS vars used by existing campaign styles
    container.classList.add('price-container--campaign');
    const cmpColor = cfg.color || '#d22e2e';
    container.style.setProperty('--campaign-price-color', cmpColor);
    container.style.setProperty('--campaign-badge-bg', cmpColor);
    container.style.setProperty('--campaign-badge-color', '#ffffff');

    // Build new HTML matching snippets/price.liquid structure
    const parts = [];
    parts.push(
      '<span role="group">' +
      '<span class="visually-hidden">Regular price&nbsp;</span>' +
      '<span class="compare-at-price">' + escapeHtml(formatMoney(priceCents, currency)) + '</span>' +
      '</span>'
    );
    parts.push(
      '<span role="group">' +
      '<span class="visually-hidden">Sale price&nbsp;</span>' +
      '<span class="price price--sale">' + escapeHtml(formatMoney(saleCents, currency)) + '</span>' +
      '</span>'
    );
    // Custom badge — compact pill on its own line below the prices.
    // Outer <div> forces a line break (block-level + flex-basis:100% handles
    // both block and flex parent layouts).
    if (cfg.badgeText && discountCents > 0) {
      const badgeRaw = String(cfg.badgeText).replace('{discount}', discountStr);
      parts.push(
        '<div class="price-campaign-badge-row" style="' +
        'width:100%;flex-basis:100%;margin-top:0.4em;' +
        '">' +
        '<span class="price-campaign-badge" style="' +
        'display:inline-block;' +
        'background-color:' + cmpColor + ';' +
        'color:#ffffff;' +
        'padding:4px 12px;' +
        'border-radius:999px;' +
        'font-size:0.85em;' +
        'font-weight:500;' +
        'line-height:1.3;' +
        'white-space:nowrap;' +
        '">' +
        escapeHtml(badgeRaw) +
        '</span>' +
        '</div>'
      );
    }

    container.innerHTML = parts.join('');
    productPrice.setAttribute(APPLIED_ATTR, 'campaign');
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // --- Process: section-level campaigns on current page --------------------

  function processSectionCampaigns(root) {
    const sections = (root || document).querySelectorAll('[data-campaign-config]');
    sections.forEach((section) => {
      let cfg;
      try { cfg = JSON.parse(section.getAttribute('data-campaign-config')); } catch (e) { return; }
      if (!cfg || !cfg.tag) return;

      // Apply to every price block inside this section
      section.querySelectorAll('product-price[data-product-tags]').forEach((pp) => {
        applyToProductPrice(pp, cfg);
      });

      // Single-product LP variant — featured-product-information sets
      // data-campaign-sticky-on-view="true" because the customer goes from
      // the LP straight to ATC (no /products/ link click happens). Writing
      // sticky immediately ensures the campaign config is in localStorage
      // by the time the customer reaches the checkout.
      if (
        section.getAttribute('data-campaign-sticky-on-view') === 'true' &&
        !section.dataset.cmpStickyWritten
      ) {
        section.dataset.cmpStickyWritten = '1';
        writeSticky(cfg);
      }

      // On click of any product link inside this section, save sticky config
      if (!section.dataset.cmpClickBound) {
        section.dataset.cmpClickBound = '1';
        section.addEventListener('click', function (e) {
          const link = e.target.closest && e.target.closest('a[href*="/products/"]');
          if (!link) return;
          writeSticky(cfg);
        }, true); // capture: fire before navigation
      }
    });
  }

  // --- Process: sticky campaign on this (non-funnel) page -----------------

  function processStickyCampaign(root) {
    const sticky = readSticky();
    if (!sticky) return;
    (root || document).querySelectorAll('product-price[data-product-tags]').forEach((pp) => {
      // Skip product-prices that already belong to a section-level campaign on this page
      if (pp.closest('[data-campaign-config]')) return;
      applyToProductPrice(pp, sticky);
    });
  }

  // --- Visibility gate: [data-visible-if-storage="key:value"] -------------
  // Resolved unconditionally on every page (independent of the campaign
  // sticky). Reads localStorage[key] directly and compares to value.

  function resolveStorageVisibility(root) {
    (root || document).querySelectorAll('[data-visible-if-storage]').forEach((el) => {
      if (el.getAttribute('data-vis-resolved')) return;
      const spec = el.getAttribute('data-visible-if-storage') || '';
      const idx = spec.indexOf(':');
      if (idx === -1) {
        // Malformed — fail safe: keep hidden.
        el.setAttribute('data-vis-resolved', 'hide');
        return;
      }
      const key = spec.slice(0, idx).trim();
      const expected = spec.slice(idx + 1).trim();
      let actual = null;
      try { actual = localStorage.getItem(key); } catch (_) {}
      el.setAttribute('data-vis-resolved', actual === expected ? 'show' : 'hide');
    });
  }

  // --- Boot & observe ------------------------------------------------------

  function run(root) {
    processSectionCampaigns(root);
    processStickyCampaign(root);
    resolveStorageVisibility(root);
  }

  function init() {
    run(document);

    // Watch for AJAX inserts (cart drawer, section render, infinite scroll, etc.)
    const SELECTOR = 'product-price, [data-campaign-config], [data-visible-if-storage]';
    const observer = new MutationObserver((mutations) => {
      let needsRun = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR)) { needsRun = true; break; }
          if (node.querySelector && node.querySelector(SELECTOR)) { needsRun = true; break; }
        }
        if (needsRun) break;
      }
      if (needsRun) run(document);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
